import React, { useEffect, useState, Fragment } from 'react';
import { withStyles } from '@material-ui/core/styles';
import styles from './dndTable.styles';
import { Typography } from '../primitives';
import Button from '@material-ui/core/Button';
import RootRef from '@material-ui/core/RootRef';
import Toolbar from '@material-ui/core/Toolbar';
import { useTheme } from '@material-ui/core/styles';
import { IconButton, Box, Grid } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {
  DragDropContext,
  Droppable,
  Draggable,
} from 'react-beautiful-dnd';
import colors from '../../utils/colors';
import DOMPurify from 'dompurify';

// Icons
import DragHandleIcon from '@material-ui/icons/DragHandle';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';

// Types
import { Column } from '../../interfaces/components';

export enum Actions {
  DELETE = 'DELETE',
}

interface Props {
  classes: { [key: string]: string };
  columns: Column[];
  data: any;
  onAfterDragEnd?: (items: any) => void;
  isDragDisabled?: boolean;
  action?: Actions;
  onActionClick?: (id: number | string | null) => void;
  isActionDisabled?: boolean;
  dataTestIdPrefix?: string;
  title: string;
}

const reorder = (list: any, startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
  ...draggableStyle,
  ...{
    height: 50,
  },
  ...(isDragging && {
    borderBottom: 0,
    height: 50,
  }),
});

const DnDTable: React.FC<Props> = ({
  classes,
  columns,
  data,
  onAfterDragEnd,
  isDragDisabled,
  action,
  onActionClick,
  isActionDisabled,
  dataTestIdPrefix,
  title,
  ...props
}) => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

  const [items, setItems] = useState<any[]>();
  const mobileColumns = columns.filter((column, index) =>
    [0].includes(index),
  );
  const [mobileEps, setMobileEps] = useState<Map<number, boolean>>(
    new Map<number, boolean>(),
  );

  const onDragProportion = 100 / (columns.length + 1);

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }
    const recentlyOrderedItems = reorder(
      items,
      result.source.index,
      result.destination.index,
    );
    setItems(recentlyOrderedItems);
    if (onAfterDragEnd) {
      onAfterDragEnd(recentlyOrderedItems);
    }
  };

  useEffect(() => {
    setItems(data);
  }, [data]);

  return (
    <div
      className={classes.holder}
      data-testid={`${dataTestIdPrefix}-dnd-holder`}
      {...props}
    >
      <Toolbar className={classes.toolBar}>
        <Typography>{title}</Typography>
      </Toolbar>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          droppableId='droppable'
          data-testid={`${dataTestIdPrefix}-dnd-droppable`}
        >
          {(provided, snapshot) => (
            <RootRef rootRef={provided.innerRef}>
              <table className={classes.table}>
                <thead>
                  <tr className={classes.tableHeadRow}>
                    {(isMobileView ? mobileColumns : columns).map(
                      (column, index) => (
                        <Fragment key={column.id}>
                          {isMobileView && (
                            <th style={{ width: 48 }} />
                          )}
                          <th
                            align={column.align}
                            style={
                              isMobileView
                                ? {}
                                : {
                                    width: column.width,
                                    textAlign: column.align,
                                    paddingLeft: !index ? 10 : 0,
                                  }
                            }
                          >
                            {column.label}
                          </th>
                        </Fragment>
                      ),
                    )}
                    {action && (
                      <th
                        style={{
                          width: isMobileView ? 'auto' : 250,
                          textAlign: 'center',
                        }}
                      >
                        Edit
                      </th>
                    )}
                    <th style={{ width: isMobileView ? 'auto' : 60 }}>
                      <span>Reorder</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items?.map((item: any, index: number) => (
                    <Draggable
                      key={item.id}
                      draggableId={item.id?.toString()!}
                      index={index}
                      isDragDisabled={isDragDisabled}
                    >
                      {(provided, snapshot) => (
                        <Fragment>
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            data-testid={`${dataTestIdPrefix}-dnd-row-${item.id}`}
                            style={getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style,
                            )}
                          >
                            {isMobileView && (
                              <td style={{ width: 48 }}>
                                <Box p={1}>
                                  <IconButton
                                    size='small'
                                    onClick={() => {
                                      setMobileEps(
                                        new Map(
                                          mobileEps.set(
                                            item.id,
                                            !mobileEps.get(item.id),
                                          ),
                                        ),
                                      );
                                    }}
                                  >
                                    {mobileEps.get(item.id) ? (
                                      <RemoveCircleOutlineIcon />
                                    ) : (
                                      <AddCircleOutlineIcon />
                                    )}
                                  </IconButton>
                                </Box>
                              </td>
                            )}
                            {(isMobileView
                              ? mobileColumns
                              : columns
                            ).map((column, index) => {
                              return (
                                <td
                                  key={column.id}
                                  align={column.align}
                                  style={
                                    isMobileView
                                      ? {}
                                      : {
                                          width: snapshot.isDragging
                                            ? `${onDragProportion}%`
                                            : column.width,
                                          paddingLeft: !index
                                            ? 10
                                            : 0,
                                        }
                                  }
                                >
                                  <Typography
                                    className={classes.tableContent}
                                  >
                                    <span
                                      dangerouslySetInnerHTML={{
                                        //DOMPurify doesn't work well with non html/svg/mathml types.
                                        __html: DOMPurify.sanitize(
                                          `<span>${
                                            item[column.id]
                                          }</span>`,
                                        ),
                                      }}
                                    ></span>
                                  </Typography>
                                </td>
                              );
                            })}
                            {action === Actions.DELETE && (
                              <td
                                align={'left'}
                                style={{
                                  textAlign: 'center',
                                  width: snapshot.isDragging
                                    ? `${onDragProportion}%`
                                    : '100%',
                                }}
                              >
                                <Button
                                  className={classes.deleteButton}
                                  data-testid={`${dataTestIdPrefix}-dnd-delete-action-${item.id}`}
                                  data-itemid={item.id}
                                  disabled={isActionDisabled}
                                  classes={{
                                    disabled: classes.disabledButton,
                                  }}
                                  onClick={(e: any) => {
                                    e.preventDefault();
                                    if (onActionClick) {
                                      onActionClick(
                                        e.currentTarget.getAttribute(
                                          'data-itemid',
                                        ),
                                      );
                                    }
                                  }}
                                >
                                  Delete
                                </Button>
                              </td>
                            )}
                            <td
                              style={{
                                textAlign: 'center',
                                width: snapshot.isDragging
                                  ? `${onDragProportion}%`
                                  : '100%',
                              }}
                            >
                              <DragHandleIcon
                                fontSize={'large'}
                                className={classes.dndIcon}
                              />
                            </td>
                          </tr>
                          {isMobileView && mobileEps.get(item.id) && (
                            <tr
                              key={`mobile-extra-info-row-${item.id}`}
                            >
                              <td
                                colSpan={columns.length + 2}
                                style={{ padding: 0 }}
                              >
                                {columns
                                  .filter(
                                    (column, index) =>
                                      ![0].includes(index),
                                  )
                                  .map((column, index) => {
                                    return (
                                      <Grid
                                        container
                                        item
                                        xs={12}
                                        key={`${column}-${index}`}
                                        style={{
                                          backgroundColor:
                                            colors.lightGold,
                                        }}
                                      >
                                        <Grid item xs={6}>
                                          <Box
                                            pl={6}
                                            pt={2}
                                            pb={2}
                                            className={
                                              classes.mobileColumnLabel
                                            }
                                          >
                                            {column.label}
                                          </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                          <Box
                                            pt={2}
                                            pb={2}
                                            className={
                                              classes.mobileColumnValue
                                            }
                                          >
                                            {item[column.id]}
                                          </Box>
                                        </Grid>
                                      </Grid>
                                    );
                                  })}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
              </table>
            </RootRef>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default withStyles(styles)(DnDTable);
